import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Auth, authState } from '@angular/fire/auth';
import { TenantAuthClaims, UserRole } from '@models';
import { Observable, catchError, first, from, of, switchMap } from 'rxjs';
import { TenantContextService } from '@services';

/**
 * Guard that checks Firebase Auth state + user roles directly from the JWT.
 * Calls getIdTokenResult() directly — self-contained, no dependency on
 * AuthService initialization order.
 *
 * Uses auth.authStateReady() to wait for Firebase to finish reading from
 * localStorage before checking the auth state. This prevents a race condition
 * where authState emits null before Firebase has finished initializing from
 * persistence (e.g., in Playwright tests that restore storageState).
 */
export function authGuard(
  roles: UserRole[],
  allowManager = false,
): CanActivateFn {
  return (): Observable<boolean | UrlTree> => {
    const auth = inject(Auth);
    const router = inject(Router);
    const tenantContext = inject(TenantContextService);

    return from(auth.authStateReady()).pipe(
      switchMap(() => authState(auth).pipe(first())),
      catchError(() => of(null)),
      switchMap((user) => {
        if (!user) return of(router.createUrlTree(['/login']));

        // Read claims directly from the JWT — no dependency on TenantContextService
        return from(user.getIdTokenResult()).pipe(
          switchMap((tokenResult) => {
            const claims = tokenResult.claims as unknown as TenantAuthClaims;

            // SUPER_ADMIN bypasses everything
            if (claims?.superAdmin === true) return of(true);

            if (roles.length === 0 && !allowManager) return of(true);

            // Resolve the *active* tenant from the switcher state, not always [0].
            // Falls back to first key (legacy or first login before any switch).
            let activeTenantId: string | null = null;
            try {
              activeTenantId = tenantContext.tenantId;
            } catch {
              activeTenantId = claims?.tenants
                ? (Object.keys(claims.tenants)[0] ?? null)
                : ((claims as any)?.tenantId ?? null);
            }
            const userRole = (
              activeTenantId && claims?.tenants?.[activeTenantId]
                ? claims.tenants[activeTenantId]
                : (claims as any)?.role
            ) as UserRole | undefined;

            if (userRole && roles.includes(userRole)) return of(true);

            if (allowManager && userRole === UserRole.MANAGER) return of(true);

            return of(router.createUrlTree(['/login']));
          }),
          catchError(() => of(router.createUrlTree(['/login']))),
        );
      }),
    );
  };
}
